// ═══════════════════════════════════════════════════════════════
// PACE — Dashboard
// Stats, completeness ring, directory assignment grid, quick links
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, SimpleGrid, Paper, Group, Stack, Button, Box,
  RingProgress, Center, ThemeIcon, Skeleton, Progress, Badge,
} from '../../components/gab/GabComponents';
import { IconAntenna, IconPhone, IconFileText, IconChartBar,
  IconActivity, IconArrowRight, IconUsers, IconBolt, IconSettings } from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { usePaceStore } from '../../store/paceStore';
import { queryRecords, createRecord } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

export default function PaceDashboard() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const { profiles, callDowns, plans, loading, loadProfiles, loadCallDowns, loadPlans } = usePaceStore();

  useEffect(() => {
    loadProfiles();
    loadPlans();
    if (activeIncident) {
      loadCallDowns(String(activeIncident.id));
    }
  }, [activeIncident?.id]);

  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="#939598">
            <IconAntenna size={30} />
          </ThemeIcon>
          <Title order={3}>No Incident Selected</Title>
          <Text size="sm" c="dimmed" ta="center" maw={420}>
            PACE communications are scoped to an incident. Pick one from the
            incident selector in the top bar, or visit the Incident Hub.
          </Text>
          <Group mt="sm">
            <Button variant="light" onClick={() => navigate('/incidents')}>Incident Hub</Button>
            <Button variant="subtle" onClick={() => navigate('/pace/profile')}>Edit My Profile</Button>
          </Group>
        </Stack>
      </Center>
    );
  }

  // Profile stats — V2 model: one profile per person, tiers are fields (primary/alternate/contingency/emergency)
  const uniquePersons = profiles.length;
  const compliantPersons = profiles.filter(p =>
    p.primary?.trim() && p.alternate?.trim() && p.contingency?.trim() && p.emergency?.trim()
  ).length;
  const partialPersons = profiles.filter(p => {
    const filled = [p.primary, p.alternate, p.contingency, p.emergency].filter(v => v?.trim()).length;
    return filled > 0 && filled < 4;
  }).length;
  const compliancePct = uniquePersons > 0 ? Math.round((compliantPersons / uniquePersons) * 100) : 0;

  const activeCallDowns = callDowns.filter(cd => cd.callStatus === 'active');

  const ringColor = compliancePct >= 80 ? 'var(--color-success)' : compliancePct >= 50 ? 'var(--data-viz-550)' : 'var(--color-error)';

  const statCards = [
    { label: 'Total Personnel', value: uniquePersons, icon: IconUsers, color: 'var(--color-neutral-600)' },
    { label: 'Fully Compliant', value: `${compliantPersons} (${compliancePct}%)`, icon: IconActivity, color: 'var(--color-success)' },
    { label: 'Active Call-Downs', value: activeCallDowns.length, icon: IconPhone, color: 'var(--data-viz-350)' },
    { label: 'Partial / Incomplete', value: partialPersons, icon: IconFileText, color: 'var(--data-viz-550)' },
  ];

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={3} c="black">PACE Communications</Title>
          <Text size="sm" c="dimmed">
            {activeIncident.name} — Primary, Alternate, Contingent, Emergency
          </Text>
        </div>
      </Group>

      {/* Org Compliance Summary */}
      <Paper p="xs" mb="sm" withBorder style={{
        borderColor: compliancePct >= 80 ? '#bbf7d0' : compliancePct >= 50 ? '#fde68a' : '#fecaca',
        background: compliancePct >= 80 ? '#f0fdf4' : compliancePct >= 50 ? '#fffbeb' : '#fef2f2',
      }}>
        <Group gap="sm" wrap="nowrap">
          <RingProgress
            size={48} thickness={4} roundCaps
            sections={[{ value: compliancePct, color: ringColor }]}
            label={
              <Text ta="center" fw={700} size="xs" style={{ color: ringColor }}>
                {compliancePct}%
              </Text>
            }
          />
          <Box style={{ flex: 1 }}>
            <Text fw={600} size="sm" style={{
              color: compliancePct >= 80 ? '#166534' : compliancePct >= 50 ? '#92400e' : '#991b1b',
            }}>
              {compliancePct >= 80 ? 'Strong PACE Compliance'
               : compliancePct >= 50 ? 'Partial Compliance — Action Needed'
               : 'Low Compliance — Profiles Incomplete'}
            </Text>
            <Text size="xs" style={{ color: compliancePct >= 80 ? '#15803d' : compliancePct >= 50 ? '#a16207' : '#b91c1c' }}>
              {compliantPersons} of {uniquePersons} personnel have all 4 tiers · {partialPersons} partially complete
            </Text>
          </Box>
          <Button variant="light" color="#03AC9E" size="compact-xs" rightSection={<IconArrowRight size={12} />}
            onClick={() => navigate('/pace/directory')}>
            View All
          </Button>
        </Group>
      </Paper>

      {/* Stat Cards */}
      {loading ? (
        <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} height={100} radius="sm" />)}
        </SimpleGrid>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md" spacing="sm">
          {statCards.map(s => (
            <Paper key={s.label} p="sm" radius="sm" withBorder ta="center"
              style={{ borderTop: `3px solid ${s.color}` }}>
              <Text size="xs" c="dimmed" fw={600}>{s.label}</Text>
              <Text fw={700} fz={28} style={{ color: s.color }}>{s.value}</Text>
            </Paper>
          ))}
        </SimpleGrid>
      )}

      {/* Compliance Bar */}
      {uniquePersons > 0 && (
        <Paper p="lg" withBorder mb="md">
          <Group justify="space-between" mb="sm">
            <Text fw={600}>Overall Compliance</Text>
            <Text size="sm" fw={700}
              c={compliancePct >= 80 ? 'var(--color-success)' : compliancePct >= 50 ? 'yellow.7' : 'var(--color-error)'}>
              {compliancePct}%
            </Text>
          </Group>
          <Progress
            value={compliancePct}
            color={compliancePct >= 80 ? 'var(--color-success)' : compliancePct >= 50 ? '#C68700' : 'var(--color-error)'}
            size="md" radius="xl"
          />
          <Text size="xs" c="dimmed" mt="xs">
            Compliance = all 4 PACE tiers filled in per person
          </Text>
        </Paper>
      )}

      {/* Directory Assignment Grid (Phase 2 — from new paceAssignments table) */}
      {TABLES.paceAssignments && (
        <Paper p="md" withBorder mb="md">
          <Group justify="space-between" mb={12}>
            <div>
              <Text fw={600} size="sm">Agency PACE Assignments</Text>
              <Text size="xs" c="dimmed">Directory-backed — P/A/C/E × function. Click Activate to initiate a call-down.</Text>
            </div>
            <Button size="compact-xs" variant="subtle" color="#939598" leftSection={<IconSettings size={12} />}
              onClick={() => navigate('/admin/people')}>
              Manage in Directory
            </Button>
          </Group>
          <DirectoryAssignmentGrid incidentId={String(activeIncident.id)} />
        </Paper>
      )}

      {/* Quick Links */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <QuickLink
          icon={<IconAntenna size={18} />}
          iconBg="#cffafe"
          title="My PACE Profile"
          subtitle={`${compliantPersons} of ${uniquePersons} fully complete`}
          onClick={() => navigate('/pace/my-pace')}
        />
        <QuickLink
          icon={<IconPhone size={18} />}
          iconBg="#dbeafe"
          title="Call-Downs"
          subtitle={`${activeCallDowns.length} active`}
          onClick={() => navigate('/pace/calldowns')}
        />
        <QuickLink
          icon={<IconFileText size={18} />}
          iconBg="#ede9fe"
          title="PACE Plans"
          subtitle={`${plans.length} plan${plans.length !== 1 ? 's' : ''}`}
          onClick={() => navigate('/pace/plans')}
        />
        <QuickLink
          icon={<IconChartBar size={18} />}
          iconBg="#fef3c7"
          title="Reports"
          subtitle="Compliance & contact sheets"
          onClick={() => navigate('/pace/reports')}
        />
      </SimpleGrid>

      {/* PACE Primer */}
      <Paper p="md" mt="lg" withBorder style={{ background: '#F8F8F8' }}>
        <Text fw={600} size="sm" c="dimmed" mb="xs">What is PACE?</Text>
        <Stack gap={6}>
          <Group gap={8}><Text span fw={700} size="xs" c="var(--color-success)" w={70}>Primary</Text><Text span size="xs" c="dimmed">Your first-choice method under normal conditions.</Text></Group>
          <Group gap={8}><Text span fw={700} size="xs" c="var(--color-primary-700)" w={70}>Alternate</Text><Text span size="xs" c="dimmed">Backup if primary fails. Should be a different method.</Text></Group>
          <Group gap={8}><Text span fw={700} size="xs" c="yellow.7" w={70}>Contingent</Text><Text span size="xs" c="dimmed">For degraded conditions when infrastructure may be impaired.</Text></Group>
          <Group gap={8}><Text span fw={700} size="xs" c="var(--color-error)" w={70}>Emergency</Text><Text span size="xs" c="dimmed">Last resort. Assume minimal or no infrastructure.</Text></Group>
        </Stack>
      </Paper>
    </div>
  );
}

// ── Directory Assignment Grid ─────────────────────────────────

type PACEAssignment = {
  record_id: number;
  personId: string; tier: string; function: string; priority: string;
  activationPhone: string; activationEmail: string; notes: string; status: string;
};

const fp = FIELDS.paceAssignments;
const TIER_COLORS_DIR: Record<string, string> = { P: 'var(--data-viz-350)', A: 'var(--data-viz-500)', C: 'var(--data-viz-550)', E: 'var(--color-error)' };
const TIER_LABELS: Record<string, string> = { P: 'Primary', A: 'Alternate', C: 'Contingency', E: 'Emergency' };

function parsePACEAssignment(r: Record<string, unknown>): PACEAssignment {
  return {
    record_id:       r.record_id as number,
    personId:        (r[fp.personId]        ?? '') as string,
    tier:            (r[fp.tier]            ?? '') as string,
    function:        (r[fp.function]        ?? '') as string,
    priority:        (r[fp.priority]        ?? '') as string,
    activationPhone: (r[fp.activationPhone] ?? '') as string,
    activationEmail: (r[fp.activationEmail] ?? '') as string,
    notes:           (r[fp.notes]          ?? '') as string,
    status:          (r[fp.status]          ?? 'active') as string,
  };
}

function DirectoryAssignmentGrid({ incidentId }: { incidentId: string }) {
  const [assignments, setAssignments] = useState<PACEAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState<number | null>(null);
  const [activated, setActivated] = useState<Set<number>>(new Set());
  const [personNames, setPersonNames] = useState<Record<string, string>>({});

  useEffect(() => {
    setLoading(true);
    queryRecords<Record<string, unknown>>(TABLES.paceAssignments, {
      filters: [{ field: fp.status, operator: 'eq', value: 'active' }],
      sort: { field: fp.priority, direction: 'asc' },
      limit: 200,
    })
      .then(async r => {
        const mapped = r.records.map(parsePACEAssignment);
        setAssignments(mapped);
        // Resolve person names for assignments that have a personId
        if (TABLES.people) {
          const ids = [...new Set(mapped.map(a => a.personId).filter(Boolean))];
          if (ids.length > 0) {
            const FPpl = FIELDS.people;
            try {
              const pRes = await queryRecords<Record<string, unknown>>(TABLES.people, {
                filters: [{ field: 'record_id', operator: 'in', value: ids.join(',') }],
                limit: ids.length,
              });
              const names: Record<string, string> = {};
              pRes.records.forEach(p => {
                const id = String(p.record_id);
                const first = String(p[FPpl.firstName] ?? '');
                const last = String(p[FPpl.lastName] ?? '');
                names[id] = [first, last].filter(Boolean).join(' ') || `Person #${id}`;
              });
              setPersonNames(names);
            } catch { /* person names are optional */ }
          }
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  async function activate(a: PACEAssignment) {
    setActivating(a.record_id);
    try {
      const fcd = FIELDS.callDowns;
      const tierLabel = TIER_LABELS[a.tier] ?? a.tier;
      await createRecord(TABLES.callDowns, {
        [fcd.title]:       `${tierLabel} — ${a.function || 'PACE Call-Down'}`,
        [fcd.message]:     `PACE activation for incident ${incidentId}. Tier: ${tierLabel}. Function: ${a.function}.`,
        [fcd.status]:      'active',
        [fcd.initiatedAt]: new Date().toISOString(),
      });
      setActivated(s => new Set(s).add(a.record_id));
    } catch (e) {
      console.error('Activate failed', e);
    } finally {
      setActivating(null);
    }
  }

  if (loading) return <Skeleton height={120} radius="sm" />;
  if (assignments.length === 0) return (
    <Paper p="md" withBorder style={{ textAlign: 'center' }}>
      <Text size="sm" c="dimmed">No PACE assignments in directory yet.</Text>
      <Text size="xs" c="dimmed" mt={4}>Add assignments from <strong>Administration → People → [person] → PACE tab</strong>.</Text>
    </Paper>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
      {(['P','A','C','E'] as const).map(tier => {
        const byTier = assignments.filter(a => a.tier === tier);
        if (byTier.length === 0) return null;
        const color = TIER_COLORS_DIR[tier];
        return (
          <Paper key={tier} radius="sm" withBorder style={{ overflow: 'hidden' }}>
            <div style={{ background: color, color: '#fff', padding: '8px 16px', fontSize: 12, fontWeight: 700 }}>
              {TIER_LABELS[tier]}
            </div>
            {byTier.map(a => {
              const isActivated = activated.has(a.record_id);
              const isBusy = activating === a.record_id;
              const personName = a.personId ? personNames[String(a.personId)] : '';
              return (
                <div key={a.record_id} style={{ padding: '8px 16px', borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{a.function || '—'}</div>
                    {personName && <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginTop: 1 }}>{personName}</div>}
                    {a.priority && <div style={{ fontSize: 12, color: '#888' }}>Priority {a.priority}</div>}
                    {a.activationPhone && <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{a.activationPhone}</div>}
                  </div>
                  <Button
                    size="compact-xs"
                    variant={isActivated ? 'filled' : 'light'}
                    color={isActivated ? 'var(--color-success)' : 'var(--color-primary-700)'}
                    leftSection={isActivated ? undefined : <IconBolt size={11} />}
                    loading={isBusy}
                    onClick={() => !isActivated && activate(a)}
                    style={{ flexShrink: 0 }}
                  >
                    {isActivated ? 'Active' : 'Activate'}
                  </Button>
                </div>
              );
            })}
          </Paper>
        );
      })}
    </div>
  );
}

function QuickLink({ icon, iconBg, title, subtitle, onClick }: {
  icon: React.ReactNode; iconBg: string; title: string; subtitle: string; onClick: () => void;
}) {
  return (
    <Paper p="md" withBorder style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16 }}
      onClick={onClick}>
      <ThemeIcon size={40} radius="md" variant="light" style={{ background: iconBg }}>
        {icon}
      </ThemeIcon>
      <Box style={{ flex: 1 }}>
        <Text fw={600} size="sm">{title}</Text>
        <Text size="xs" c="dimmed">{subtitle}</Text>
      </Box>
      <IconArrowRight size={14} color="#9ca3af" />
    </Paper>
  );
}
